import { DateTime } from 'luxon';
import {
  formatDateTimeShort,
  formatter,
  renderProviderName,
  SystemProblem,
  SystemProblemDetailsMeta,
} from '@vgrubs/components';
import {
  SAAS_ELIGIBLE_SERVICES,
  SAAS_ELIGIBLE_TOTAL_THRESHOLD,
} from '../../Dashboard/sections/FinancesSection/saasEligibility';
import { PRINTER_ERROR_LABEL, printerKindLabel } from './printerLabels';
import { formatProblemAge } from './systemProblemLabels';

/** One fact in the detail: a DetailRow's label, value and quieter line. */
export type ProblemFact = {
  label: string;
  value: string;
  hint?: string | null;
  /** Shown with the provider's logo at the row's right edge. */
  provider?: string;
};

const CONNECTION_TYPE_LABEL: Record<string, string> = {
  foodhub: 'POS Hub',
  direct: 'Direct',
  thirdParty: 'KitchenHub',
};

const FLEET_SOURCE_LABEL: Record<string, string> = {
  firstdelivery: 'First Delivery',
  cartwheel: 'Cartwheel',
};

const HOURS_LABEL: Record<string, string> = {
  delivery: "the brand's delivery hours",
  business: "the brand's business hours",
  holiday: "the day's holiday hours",
};

const dollars = (cents: number) => formatter.format(cents / 100);

/**
 * saasNotEligible: what the location pays, then what vGrubs brought it, each
 * beside the amount that alone would make it eligible and how far off it is.
 * Thresholds are the SaaS eligible list's, which the backend shares.
 */
const saasFacts = (meta: SystemProblemDetailsMeta): ProblemFact[] => {
  const days = meta.days ?? 30;
  const pays = [
    meta.monthlyAmount ? `${dollars(meta.monthlyAmount)} a month` : null,
    meta.vOrdersPerOrderFee != null
      ? `${dollars(meta.vOrdersPerOrderFee)} per order`
      : null,
    meta.vOrdersPerDayFee != null
      ? `${dollars(meta.vOrdersPerDayFee)} per day`
      : null,
  ].filter(Boolean);

  const brought = (
    value: number,
    threshold: number,
    strictly = false
  ): string => {
    const needs = `Needs ${strictly ? 'over ' : ''}${dollars(threshold)}`;
    return threshold > value
      ? `${needs} · ${dollars(threshold - value)} short`
      : needs;
  };

  const facts: ProblemFact[] = [
    {
      label: 'Pays for SaaS',
      value: pays.length ? pays.join(' + ') : dollars(0),
      hint: meta.monthlyAmount
        ? 'Without extra tablets and printers'
        : pays.length
        ? 'vTablet billed per order or per day'
        : null,
    },
  ];
  /* Recovered first: the order the backend's own sentence reads in. */
  const services = [...SAAS_ELIGIBLE_SERVICES].reverse();
  let total = 0;
  services.forEach(({ key, label, threshold }) => {
    const value = meta[key] ?? 0;
    total += value;
    facts.push({
      label: `${label}, last ${days} days`,
      value: dollars(value),
      hint: brought(value, threshold),
    });
  });
  facts.push({
    label: 'All three together',
    value: dollars(total),
    hint: brought(total, SAAS_ELIGIBLE_TOTAL_THRESHOLD, true),
  });
  return facts;
};

/** An exact time, and how long ago while "ago" still means something. */
const when = (iso: string) => {
  const age = formatProblemAge(iso);
  const exact = formatDateTimeShort(iso);
  return age === exact ? exact : `${exact} (${age})`;
};

/**
 * The facts a problem's `meta` carries, in reading order. Every field is
 * optional and only what is there is shown, so one renderer serves every type
 * (see `SystemProblemDetailsMeta`) - including one this build does not know.
 * The duplicate's meta is drawn by ConnectionMap instead.
 */
export const problemFacts = (problem: SystemProblem): ProblemFact[] => {
  if (problem.type === 'duplicateStoreConnection' || !problem.meta) return [];
  const meta = problem.meta as SystemProblemDetailsMeta;
  if (problem.type === 'saasNotEligible') return saasFacts(meta);
  const facts: ProblemFact[] = [];

  if (meta.printerKey) {
    facts.push({
      label: 'Printer',
      value: meta.name || 'Unnamed printer',
      hint: printerKindLabel(meta.brand, meta.connection) || null,
    });
  }
  if (meta.errorCode) {
    facts.push({
      label: 'Printer error',
      value: PRINTER_ERROR_LABEL[meta.errorCode] ?? meta.errorCode,
      hint: [meta.message, meta.errorSince && `since ${when(meta.errorSince)}`]
        .filter(Boolean)
        .join(' · '),
    });
  }

  /* A menu publish names its provider on the Menu row instead. */
  if (meta.provider && !meta.menuName && !meta.brandMenuId) {
    facts.push({
      label: 'Provider',
      value: renderProviderName(meta.provider) ?? meta.provider,
      hint: meta.storeId ? `Store ID ${meta.storeId}` : null,
      provider: meta.provider,
    });
  }

  if (meta.menuName || meta.brandMenuId) {
    facts.push({
      label: 'Menu',
      value: meta.menuName || `Menu #${meta.brandMenuId}`,
      hint: [
        meta.connectionType &&
          (CONNECTION_TYPE_LABEL[meta.connectionType] ?? meta.connectionType),
        /* A failed publish with no provider went to the whole connection. */
        meta.provider
          ? renderProviderName(meta.provider)
          : problem.type === 'menuPublishFailed'
          ? 'all providers'
          : null,
      ]
        .filter(Boolean)
        .join(' · '),
      provider: meta.provider ?? undefined,
    });
  }
  if (meta.error) facts.push({ label: 'Error', value: meta.error });
  /* Only working hours KitchenHub refused carry these; null = no answer. */
  if (meta.status !== undefined) {
    facts.push({
      label: 'KitchenHub response',
      value: meta.status === null ? 'No response' : `HTTP ${meta.status}`,
      hint: meta.withOverrides ? 'Sent with delivery/pickup overrides' : null,
    });
  }
  if (meta.failedAt) {
    facts.push({ label: 'Failed', value: when(meta.failedAt) });
  }
  if (meta.inProgressSince) {
    facts.push({
      label: 'In progress since',
      value: when(meta.inProgressSince),
    });
  }
  if (meta.pendingSince) {
    facts.push({ label: 'Pending since', value: when(meta.pendingSince) });
  }

  if (meta.reasons?.length) {
    facts.push({
      label: 'Why no fleet took it',
      value: meta.reasons.join(', '),
      hint: meta.source
        ? `Checked with ${FLEET_SOURCE_LABEL[meta.source] ?? meta.source}`
        : null,
    });
  }

  if (meta.amount !== undefined) {
    facts.push({
      label: 'Recharge amount',
      value: formatter.format(meta.amount / 100),
      hint: meta.covered
        ? 'Covered by the vGrubs balance'
        : 'Not covered - rides go on overdraft',
    });
  }

  if (meta.lastSeenAt) {
    facts.push({ label: 'Tablet last online', value: when(meta.lastSeenAt) });
  }

  /* Read in the location's own time zone, which is what "should be open"
     was judged by. */
  if (meta.localTime) {
    const local = DateTime.fromISO(meta.localTime, { setZone: true });
    facts.push({
      label: 'Checked at',
      value: `${local.toFormat('MMM d, h:mm a')}${
        meta.timeZone ? ` (${meta.timeZone})` : ''
      }`,
      hint: meta.hours
        ? `Against ${HOURS_LABEL[meta.hours] ?? meta.hours}`
        : null,
    });
  }

  if (meta.integrationAccountId) {
    facts.push({
      label: 'KitchenHub integration account',
      value: meta.integrationAccountId,
    });
  }

  return facts;
};
